import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SimpleTag, SimpleTagDirectory } from "src/api/generated/frontend_internal_v1";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { UUID } from "src/api/api_common";
import WS from "src/api/ws";

/**
 * The properties for {@link MoveTagFromDirectoryDialog}
 */
export type MoveTagFromDirectoryDialogProps = {
    /** The tag to add */
    tag: SimpleTag | undefined;

    /** Whether the dialog is open */
    isOpen: boolean;
    /**
     * Callback when the form is submitted
     *
     * The parameter `directory_uuid` contains the directory the tag has been moved to
     * or `undefined` if the tag has been removed from its previous directory without moving it to a new one.
     */
    onSubmit: (directory_uuid: UUID | undefined) => void;

    /** Callback for closing the popup */
    onClose: () => void;
};

/**
 * A dialog to add a tag to a directory
 */
export default function MoveTagFromDirectoryDialog(props: MoveTagFromDirectoryDialogProps) {
    const [t] = useTranslation("dialog-tag-directory");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            directory_uuid: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (
                    value.directory_uuid !== "ungrouped" &&
                    !directories.map((dir) => dir.uuid).includes(value.directory_uuid)
                ) {
                    value.directory_uuid = "";
                    return { fields: { directory_uuid: t("error.no-target-selected") } };
                }

                if (!props.tag) return;
                const directory_uuid = value.directory_uuid === "ungrouped" ? undefined : value.directory_uuid;
                await ApiInternal.tags.moveTagToDirectory({
                    tag_uuid: props.tag.uuid,
                    directory_uuid,
                });
                props.onSubmit(directory_uuid);
            },
        },
    });

    const [directories, setDirectories] = React.useState<SimpleTagDirectory[]>([]);
    const [refreshKey, setRefreshKey] = React.useState(0);

    // Trigger a bump of the counter "refreshKey" when tags change, so that it re-fetches the directories in the second useEffect
    useEffect(() => {
        const listener = WS.addEventListener("message.TagsChanged", () => {
            setRefreshKey((k) => k + 1);
        });
        return () => WS.removeEventListener(listener);
    }, [props.isOpen]);

    useEffect(() => {
        let cancelled = false;
        ApiInternal.tags.getAllDirectories().then((res) => {
            if (!cancelled) setDirectories(res.list);
        });
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.tag]);

    if (!props.tag) return null;

    const targetsEmpty =
        !(props.tag && props.tag.directory) && !directories?.some((d) => d.uuid !== props.tag?.directory);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.update-tag-to-directory")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field
                                name={"directory_uuid"}
                                validators={{
                                    onChange: ({ value }) => (value === "" ? t("error.no-target-selected") : undefined),
                                }}
                            >
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.directory")}</RequiredLabel>
                                        <Listbox
                                            value={fieldApi.state.value}
                                            disabled={targetsEmpty}
                                            onChange={(e) => fieldApi.handleChange(e)}
                                        >
                                            {props.tag && props.tag.directory && (
                                                <ListboxOption value={"ungrouped"}>
                                                    <ListboxLabel>
                                                        <span className={"pr-0.5 italic"}>{t("label.ungrouped")}</span>
                                                    </ListboxLabel>
                                                </ListboxOption>
                                            )}
                                            {directories
                                                .filter((d) => d.uuid !== props.tag?.directory)
                                                .map((d) => (
                                                    <ListboxOption value={d.uuid} key={d.uuid}>
                                                        <ListboxLabel>{d.name}</ListboxLabel>
                                                    </ListboxOption>
                                                ))}
                                        </Listbox>
                                        {targetsEmpty && (
                                            <ErrorMessage>{t("error.no-valid-targets-exist")}</ErrorMessage>
                                        )}
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.update-tag")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
